import { useState } from 'react'
import { Navigation, Wind, Cloud, Eye, Activity, MapPin, Clock, ChevronRight } from 'lucide-react'
import { formatAltitude, formatSpeed, formatHeading, formatVerticalRate, getAirlineFromCallsign } from '../lib/utils'
import AirlineLogo from './AirlineLogo'

export default function DashboardDisplay({ flights, stats, weather }) {
  const [selectedFlight, setSelectedFlight] = useState(null)
  const activeFlight = selectedFlight || flights[0]
  
  return (
    <div className="h-screen w-screen bg-slate-950 flex overflow-hidden">
      <div className="w-80 bg-slate-900/80 border-r border-slate-800 flex flex-col">
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-2 text-flight-cyan mb-1">
            <Activity className="w-4 h-4" />
            <span className="text-sm font-semibold tracking-wide">LIVE SECTOR</span>
          </div>
          <div className="text-2xl font-bold text-white">{stats.total} <span className="text-slate-500 text-sm font-normal">aircraft</span></div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {flights.map((f, i) => {
            const airline = getAirlineFromCallsign(f.callsign)
            return (
              <button key={f.icao24 || i} onClick={() => setSelectedFlight(f)}
                className={`w-full text-left p-3 border-b border-slate-800/50 transition-all hover:bg-slate-800/50 ${activeFlight?.icao24 === f.icao24 ? 'bg-slate-800/80 border-l-2 border-l-flight-cyan' : ''}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white font-mono font-bold text-sm">{f.callsign}</span>
                  <span className="text-slate-500 text-xs">{f.distance} NM</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <span>{airline.name}</span><span className="text-slate-600">•</span><span>{formatAltitude(f.altitude)}</span>
                </div>
              </button>
            )
          })}
        </div>
        {weather && (
          <div className="p-4 border-t border-slate-800 bg-slate-900/50">
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-2">
              <Cloud className="w-3 h-3" /><span>METAR {weather.icao}</span>
            </div>
            <div className="text-slate-300 text-sm font-mono">{weather.raw?.substring(0, 40)}...</div>
          </div>
        )}
      </div>
      <div className="flex-1 flex flex-col">
        <div className="h-16 bg-slate-900/60 border-b border-slate-800 flex items-center px-6 gap-8">
          <StatItem icon={<Wind className="w-4 h-4" />} label="AVG ALT" value={formatAltitude(stats.avgAltitude)} />
          <StatItem icon={<Navigation className="w-4 h-4" />} label="AVG SPD" value={formatSpeed(stats.avgSpeed * 2.23694)} />
          <StatItem icon={<Eye className="w-4 h-4" />} label="RANGE" value="20 NM" />
          <div className="ml-auto flex items-center gap-2 text-slate-500 text-xs">
            <Clock className="w-3 h-3" />{new Date().toLocaleTimeString('en-GB')}
          </div>
        </div>
        {activeFlight && (
          <div className="flex-1 p-8 flex flex-col justify-center">
            <div className="max-w-4xl mx-auto w-full">
              <div className="flex items-start justify-between mb-8">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <AirlineLogo iata={getAirlineFromCallsign(activeFlight.callsign).iata} />
                    <div>
                      <h1 className="text-4xl font-bold text-white tracking-tight">{activeFlight.callsign}</h1>
                      <p className="text-slate-400">{getAirlineFromCallsign(activeFlight.callsign).name}</p>
                    </div>
                  </div>
                  {activeFlight.route && (
                    <div className="flex items-center gap-3 mt-4 text-lg">
                      <span className="text-white font-mono font-bold">{activeFlight.route[0]}</span>
                      <ChevronRight className="w-5 h-5 text-flight-cyan" />
                      <span className="text-white font-mono font-bold">{activeFlight.route[1]}</span>
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-slate-500 text-sm mb-1">Aircraft Type</div>
                  <div className="text-white font-mono text-lg">{activeFlight.aircraftType || activeFlight.aircraftTypeCode || '---'}</div>
                  <div className="text-slate-500 text-sm mt-2 mb-1">ICAO24</div>
                  <div className="text-white font-mono">{activeFlight.icao24}</div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <DetailCard label="Altitude" value={formatAltitude(activeFlight.altitude)} sub={activeFlight.on_ground ? 'On Ground' : 'Barometric'} color="cyan" />
                <DetailCard label="Ground Speed" value={formatSpeed(activeFlight.velocity ? activeFlight.velocity * 2.23694 : 0)} sub="True Airspeed est." color="amber" />
                <DetailCard label="Track / Heading" value={formatHeading(activeFlight.true_track)} sub="Magnetic" color="emerald" />
                <DetailCard label="Vertical Rate" value={formatVerticalRate(activeFlight.vertical_rate)} sub="Feet per minute" color="rose" />
              </div>
              <div className="mt-6 glass-panel p-4 flex items-center gap-4">
                <MapPin className="w-5 h-5 text-flight-cyan" />
                <div>
                  <div className="text-slate-400 text-xs">POSITION</div>
                  <div className="text-white font-mono">{activeFlight.latitude?.toFixed(4)}°, {activeFlight.longitude?.toFixed(4)}°</div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-slate-400 text-xs">DISTANCE FROM YOU</div>
                  <div className="text-white font-mono text-lg">{activeFlight.distance} NM</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function StatItem({ icon, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <div className="text-slate-500">{icon}</div>
      <div>
        <div className="text-slate-500 text-[10px] tracking-wider">{label}</div>
        <div className="text-white font-mono text-sm font-bold">{value}</div>
      </div>
    </div>
  )
}

function DetailCard({ label, value, sub, color }) {
  const colors = { cyan: 'border-flight-cyan/30 bg-flight-cyan/5', amber: 'border-flight-amber/30 bg-flight-amber/5', emerald: 'border-flight-emerald/30 bg-flight-emerald/5', rose: 'border-flight-rose/30 bg-flight-rose/5' }
  return (
    <div className={`rounded-xl border p-5 ${colors[color]}`}>
      <div className="text-slate-400 text-xs tracking-wider mb-2">{label}</div>
      <div className="text-white text-2xl font-mono font-bold">{value}</div>
      <div className="text-slate-500 text-xs mt-1">{sub}</div>
    </div>
  )
}
